// import React from 'react';
import { styled } from '@mui/material/styles';
import { AdverseNewsMonthCountDto, AffinityDto } from 'models/dtoTypes';
import SubSection from './_subSection';
import SdgScoreClob, { ISdgData } from './_sdgScoreClob';
import Empty from './_empty';
import NewsBarchart from './_newsBarchart';
import { newsCheck } from 'kosmos/images';
import EsgLegand from './_esgLegand';

const PREFIX = '_newsCheckReport';

const classes = {
  report: `${PREFIX}-report`,
  headers: `${PREFIX}-headers`,
  up: `${PREFIX}-up`,
  down: `${PREFIX}-down`,
  even: `${PREFIX}-even`,
  row: `${PREFIX}-row`,
  indicator: `${PREFIX}-indicator`,
  trend: `${PREFIX}-trend`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  [`& .${classes.report}`]: {
  },

  [`& .${classes.row}`]: {
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.headers}`]: {
    display: 'flex',
  },

  [`& .${classes.up}`]: {
    width: 0,
    height: 0,
    borderLeft: '15px solid #68696c',
    borderRight: '15px solid #68696c',
    borderBottom: '30px solid white',
  },

  [`& .${classes.down}`]: {
    width: 0,
    height: 0,
    borderLeft: '15px solid #68696c',
    borderRight: '15px solid #68696c',
    borderTop: '30px solid white',
  },

  [`& .${classes.even}`]: {
    width: 0,
    height: 0,
    marginTop: 10,
    borderTop: '5px solid white',
    borderLeft: '15px solid white',
    borderRight: '15px solid white',
    borderBottom: '5px solid white',
  },

  [`& .${classes.indicator}`]: {
    flex: 3,
    marginRight: 50,
  },

  [`& .${classes.trend}`]: {
    flex: 1,
  },
}));

enum ETrend {
  up = 'up',
  down = 'down',
  even = 'even',
}

interface Indicator {
  title: string;
  affinity: AffinityDto;
  countryTrend: ETrend;
  geoTrend: ETrend;
}

interface IProps {
  // newsCheckReport: DualUseGoodsClobDto[];
  indicators?: Indicator[];
  adverseNewsReport: AdverseNewsMonthCountDto[];
  enableBarChart: boolean;
  enableTrendChart: boolean;
}

const example = [
  {
    title: 'indicator one',
    affinity: 'positive',
    countryTrend: ETrend.up,
    geoTrend: ETrend.down,
  },
  {
    title: 'indicator two',
    affinity: 'negative',
    countryTrend: ETrend.up,
    geoTrend: ETrend.down,
  },
  {
    title: 'indicator three',
    affinity: 'warning',
    countryTrend: ETrend.up,
    geoTrend: ETrend.even,
  },
  {
    title: 'indicator three',
    affinity: 'neutral',
    countryTrend: ETrend.even,
    geoTrend: ETrend.even,
  },
];

export default function NewsCheckReport(props: IProps): JSX.Element {
  const { adverseNewsReport, enableBarChart, enableTrendChart, indicators = example } = props;

  const renderInner = (trend = ETrend.even) => {
    switch(trend) {
      case ETrend.up:
        return <div className={classes.up} />;
      case ETrend.down:
        return <div className={classes.down} />;
      default:
        return <div className={classes.even} />;
    }
  };

  const renderTrend = (trend = ETrend.even) => {
    return (
      <div style={{ padding: 10, width: 50, height: 50, backgroundColor: '#68696c' }}>
        {renderInner(trend)}
      </div>
    );
  };

  const renderSingleItem = (item: Indicator, index: number) => {
    const { title, affinity, countryTrend, geoTrend } = item;

    return (
      <div className={classes.row} key={index}>
        <div className={classes.indicator}>
          <SdgScoreClob
            withBorder={true}
            key={index}
            title={title}
            affinity={affinity}
          />
        </div>
        <div className={classes.trend}>{renderTrend(countryTrend)}</div>
        <div className={classes.trend}>{renderTrend(geoTrend)}</div>
      </div>
    );
  };

  const renderLegands = () => {
    const legands: ISdgData[] = [
      {
        affinity: 'positive',
        title: 'Low risk found from corresponding indicator.',
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'negative',
        title: 'High risk found from corresponding indicator.',
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'warning',
        title: 'Neutral risk found from corresponding indicator.',
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'neutral',
        title: 'No enough information available.',
        withBg: true,
        size: 'small',
      },
    ];

    return (
      <EsgLegand
        legands={legands}
        title="Political risk indicators"
      />
    );
  };

  const renderContent = () => {
    if (indicators.length === 0) {
      return <Empty title="There is no data available"/>;
    }

    return (
      <Root>
        <div className={classes.headers}>
          <h3 style={{ flex: 3 }}>Against country benchmark</h3>
          <h3 style={{ flex: 1 }}>Country trend</h3>
          <h3 style={{ flex: 1 }}>Geolocation trend</h3>
        </div>
        {indicators.map((item, index) => renderSingleItem(item as Indicator, index))}
      </Root>
    );
  };

  const renderBarChart = () => {
    const hasData = adverseNewsReport.find(m => m.count);
    if (!hasData) {
      return (
        <Empty title="No adverse news found. "/>
      );
    }


    const data = adverseNewsReport.map((item: AdverseNewsMonthCountDto) => {
      // to resolve Index signature for type 'string' missing in AdverseNewsMonthCountDto
      return {
        ...item
      };
    });

    return (
      <div style={{ height: 500 }}>
        <NewsBarchart
          data={data}
          keys={['count']}
          indexBy="monthLabel"
        />
      </div>
    );
  };

  const renderTrendChart = () => {
    return (
      <>
        {renderLegands()}
        {renderContent()}
      </>
    );
  };

  return (
    <SubSection
      title="Company Adverse News"
      isBold
      icon={<img src={newsCheck} />}
    >
      <>
        {enableBarChart && renderBarChart()}
        <p>Adverse news taken from Bureau Van Dijk, Informs, Syndicate, Reuters</p>
        {enableTrendChart && renderTrendChart()}
      </>
    </SubSection>
  );
}

